import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@material-ui/core';
import { ArrowBackRounded as BackIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useLocation, useParams } from 'react-router';
import moment from 'moment';
import styled from 'styled-components';
import { Alert } from '@material-ui/lab';
import { Document, Page } from 'react-pdf';
import { getExpenseClaimDisplayDate } from 'screens/expense-claim-date';

import {
  AuthContext,
  baseUrl,
  ensurePdfJsWorker,
  getFileAccessUrls,
  isOrgFeatureEnabled,
  useFile,
  useSpot
} from 'framework';
import { ExpenseClaimDetail, ExpenseClaimStatus, LineItem } from 'domain-types';
import {
  Container,
  ConfirmDialog,
  Gap,
  Line,
  Row,
  Spacer,
  Table,
  TitleContainer
} from 'components';

const SectionTitle = styled(Typography)`
  margin: 12px 0;
`;

const DocumentScrollBox = styled(Box)`
  max-height: 55vh;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${p => p.theme.spacing(4, 0)};
  background-color: ${p => p.theme.palette.background.default};
  width: 100%;
`;

const ReceiptPreviewImage = styled.img`
  max-width: 100%;
  max-height: 60vh;
`;

ensurePdfJsWorker();

const getClaimStatusLabel = (
  status: ExpenseClaimStatus,
  t: (key: string) => string
) => {
  switch (status) {
    case 'draft':
      return t('claimDraft');
    case 'approved_sent':
      return t('claimApprovedSent');
    case 'rejected':
      return t('claimRejected');
    case 'retracted':
      return t('claimRetracted');
    case 'send_failed':
      return t('claimSendFailed');
    default:
      return t('claimPendingApproval');
  }
};

const safeDecodeURIComponent = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const buildExpenseClaimDetailEndpoint = (
  organization: string,
  userId: string,
  expenseClaimId: string
) =>
  `${baseUrl}/org/${organization}/user/${encodeURIComponent(
    userId
  )}/expenses/${encodeURIComponent(safeDecodeURIComponent(expenseClaimId))}`;

export const getExpenseReceiptPreviewType = (filename: string) => {
  if (/\.(png|jpg|gif|jpeg|bmp|svg)$/i.test(filename)) {
    return 'image';
  }
  if (/\.pdf$/i.test(filename)) {
    return 'pdf';
  }
  return 'download';
};

const getReceiptLocation = (receiptKey: string) => {
  const [ownerUserId, ...segments] = receiptKey.split('/');
  if (!ownerUserId || segments.length === 0) {
    return null;
  }
  return {
    ownerUserId,
    keyRelativeToUserRoot: segments.join('/')
  };
};

export function ExpenseClaimDetailScreen() {
  const { t } = useTranslation();
  const history = useHistory();
  const { organization, expenseClaimId } = useParams<{
    organization: string;
    expenseClaimId: string;
  }>();
  const location = useLocation();
  const { getCurrentUser, getToken } = useContext(AuthContext);
  const { data, query, command } = useSpot();
  const [claim, setClaim] = useState<ExpenseClaimDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeReceiptKey, setActiveReceiptKey] = useState('');
  const [receiptError, setReceiptError] = useState('');
  const [receiptPageCount, setReceiptPageCount] = useState(0);
  const [retractDialogOpen, setRetractDialogOpen] = useState(false);

  const userId = getCurrentUser();
  const org = data?.organizations?.[organization];
  const expensesEnabled = org ? isOrgFeatureEnabled(org, 'expenses') : true;

  useEffect(() => {
    if (!org) {
      query(`org/${organization}`, {}, ['organizations', organization]);
    }
  }, [org, organization, query]);

  const fetchClaim = useCallback(async () => {
    // Use caller id in route; backend authorizes employee/manager/admin scope.
    const endpoint = buildExpenseClaimDetailEndpoint(
      organization,
      userId,
      expenseClaimId
    );
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        authorization: getToken().getJwtToken()
      }
    });
    if (!response.ok) {
      throw new Error('Failed to load claim detail');
    }
    const payload = (await response.json()) as ExpenseClaimDetail;
    setClaim(payload);
  }, [expenseClaimId, getToken, organization, userId]);

  useEffect(() => {
    (async () => {
      if (!organization || !org) {
        setLoading(false);
        return;
      }

      if (!expensesEnabled) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      setClaim(null);
      try {
        await fetchClaim();
      } catch {
        setError(t('expenseClaimLoadFailed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [expensesEnabled, fetchClaim, org, organization, t]);

  const downloadReceipt = async (
    ownerUserId: string,
    keyRelativeToUserRoot: string,
    filename: string
  ) => {
    const { cacheUrl } = await getFileAccessUrls(
      keyRelativeToUserRoot,
      organization,
      getToken().getJwtToken(),
      ownerUserId
    );
    const link = document.createElement('a');
    link.href = cacheUrl;
    link.download = filename;
    link.click();
  };

  const lineTotal = (item: LineItem) =>
    item.kmTraveled !== undefined && item.kmRate !== undefined
      ? `€${(item.kmTraveled * item.kmRate).toFixed(2)}`
      : `€${item.amountIncl.toFixed(2)}`;

  const onBack = () => {
    if (location.key !== 'default') {
      history.goBack();
      return;
    }
    const source = new URLSearchParams(location.search).get('from');
    history.push(
      source === 'actions'
        ? `/${organization}/actions`
        : `/${organization}/expenses`
    );
  };

  useEffect(() => {
    setActiveReceiptKey(currentKey => {
      if (!claim?.receipts?.length) {
        return '';
      }
      return claim.receipts.some(receipt => receipt.key === currentKey)
        ? currentKey
        : claim.receipts[0].key;
    });
  }, [claim]);

  const activeReceipt =
    claim?.receipts.find(receipt => receipt.key === activeReceiptKey) ?? null;
  const activeReceiptLocation = activeReceipt
    ? getReceiptLocation(activeReceipt.key)
    : null;
  const {
    url: activeReceiptUrl,
    loading: receiptLoading,
    error: activeReceiptLoadError
  } = useFile(
    activeReceiptLocation?.keyRelativeToUserRoot ?? '',
    organization,
    activeReceiptLocation?.ownerUserId
  );

  useEffect(() => {
    if (!activeReceipt) {
      setReceiptError('');
      return;
    }

    setReceiptPageCount(0);

    if (!activeReceiptLocation) {
      setReceiptError(t('expenseReceiptPreviewFailed'));
      return;
    }

    setReceiptError('');
  }, [activeReceipt, activeReceiptLocation, t]);

  useEffect(() => {
    if (activeReceiptLoadError) {
      setReceiptError(t('expenseReceiptPreviewFailed'));
    }
  }, [activeReceiptLoadError, t]);

  const canRetractClaim =
    claim?.createdByUserId === userId &&
    ['pending_approval', 'approved_sent', 'send_failed'].includes(claim.status);

  if (org && !expensesEnabled) {
    return <Redirect to={`/${organization}`} />;
  }

  const renderReceiptPreview = () => {
    if (!activeReceipt) {
      return null;
    }

    if (receiptLoading) {
      return <Typography>{t('loading')}</Typography>;
    }

    if (receiptError) {
      return <Typography color="error">{receiptError}</Typography>;
    }

    if (getExpenseReceiptPreviewType(activeReceipt.name) === 'image') {
      return (
        <ReceiptPreviewImage
          src={activeReceiptUrl}
          alt={activeReceipt.name}
          data-testid="expense-receipt-image-preview"
          onError={() => setReceiptError(t('expenseReceiptPreviewFailed'))}
        />
      );
    }

    if (getExpenseReceiptPreviewType(activeReceipt.name) === 'pdf') {
      return (
        <DocumentScrollBox data-testid="expense-receipt-pdf-preview">
          <Document
            file={activeReceiptUrl}
            onLoadSuccess={({ numPages }: { numPages: number }) =>
              setReceiptPageCount(numPages)
            }
            onLoadError={() =>
              setReceiptError(t('expenseReceiptPreviewFailed'))
            }
          >
            {/* Show only the first page to keep receipt preview fast. */}
            <Page pageNumber={1} />
          </Document>
          {receiptPageCount > 1 && (
            <Typography
              variant="body2"
              aria-label={String(
                t('receiptPageIndicator', {
                  current: 1,
                  total: receiptPageCount
                })
              )}
            >
              {`1 / ${receiptPageCount}`}
            </Typography>
          )}
        </DocumentScrollBox>
      );
    }

    return <Typography>{t('noPreviewDownload')}</Typography>;
  };

  if (loading) {
    return (
      <Container>
        <Typography>{t('loading')}</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!claim) {
    return (
      <Container>
        <Typography>{t('expenseClaimLoadFailed')}</Typography>
      </Container>
    );
  }

  return (
    <Container>
      <TitleContainer>
        <Typography variant="h5">{t('expenseClaimDetail')}</Typography>
      </TitleContainer>
      <Line />
      <Row mb={1}>
        <IconButton title={t('back')} aria-label={t('back')} onClick={onBack}>
          <BackIcon />
        </IconButton>
        <Spacer />
        {canRetractClaim ? (
          <Button color="primary" onClick={() => setRetractDialogOpen(true)}>
            {t('retractExpenseClaim')}
          </Button>
        ) : null}
      </Row>

      <Table
        headers={[t('status'), t('date'), t('iban'), t('total')]}
        rows={[
          {
            id: 'summary',
            cells: [
              getClaimStatusLabel(claim.status, t),
              moment(getExpenseClaimDisplayDate(claim)).format('LL'),
              claim.ibanLast4 ? `****${claim.ibanLast4}` : '-',
              `€${claim.totalAmountIncl.toFixed(2)}`
            ]
          }
        ]}
      />
      {claim.status === 'retracted' ? (
        <>
          <Gap />
          <Alert severity="info">
            {claim.retractedAt
              ? t('expenseClaimRetractedOn', {
                  date: moment(claim.retractedAt).format('LLL')
                })
              : t('claimRetracted')}
          </Alert>
        </>
      ) : null}

      <Gap />
      <SectionTitle variant="h6">{t('lineItems')}</SectionTitle>
      <Table
        headers={[
          t('date'),
          t('description'),
          t('vat'),
          t('amountIncl'),
          t('kmTraveled'),
          t('kmRate'),
          t('kmTotal')
        ]}
        rows={claim.lineItems.map((item, index) => ({
          id: `${claim.id}-line-${index}`,
          cells: [
            moment(item.date).format('LL'),
            item.description,
            item.vat !== undefined ? `€${item.vat.toFixed(2)}` : '-',
            `€${item.amountIncl.toFixed(2)}`,
            item.kmTraveled !== undefined ? item.kmTraveled : '-',
            item.kmRate !== undefined ? `€${item.kmRate.toFixed(2)}` : '-',
            lineTotal(item)
          ]
        }))}
      />

      <Gap />
      <SectionTitle variant="h6">{t('receipts')}</SectionTitle>
      <Table
        headers={[t('name'), t('date'), t('preview'), t('download')]}
        rows={claim.receipts.map((receipt, index) => ({
          id: `${claim.id}-receipt-${index}`,
          cells: [
            receipt.name,
            receipt.uploadedAt ? moment(receipt.uploadedAt).format('LLL') : '-',
            <Button
              key={`preview-${receipt.key}`}
              color="primary"
              onClick={event => {
                event.stopPropagation();
                setActiveReceiptKey(receipt.key);
              }}
            >
              {t('preview')}
            </Button>,
            <Button
              key={`dl-${receipt.key}`}
              color="primary"
              onClick={event => {
                event.stopPropagation();
                const receiptLocation = getReceiptLocation(receipt.key);
                if (!receiptLocation) {
                  return;
                }
                downloadReceipt(
                  receiptLocation.ownerUserId,
                  receiptLocation.keyRelativeToUserRoot,
                  receipt.name
                );
              }}
            >
              {t('download')}
            </Button>
          ]
        }))}
      />
      {activeReceipt && (
        <>
          <Gap />
          <SectionTitle variant="h6">{t('receiptPreview')}</SectionTitle>
          {renderReceiptPreview()}
        </>
      )}
      <Box mt={2} />
      <ConfirmDialog
        title={t('retractExpenseTitle')}
        text={t('retractExpenseText')}
        dialogOpen={retractDialogOpen}
        acceptLabel={t('retractExpenseClaim')}
        onClose={() => setRetractDialogOpen(false)}
        onSubmit={async () => {
          if (!claim) {
            return;
          }

          await command(
            `org/${organization}/user/${userId}/expenses/${encodeURIComponent(
              claim.id
            )}/retract`,
            {}
          );
          await query(`org/${organization}/user/${claim.createdByUserId}/expenses`, {}, [
            'expenses',
            claim.createdByUserId
          ]);
          await fetchClaim();
        }}
      />
    </Container>
  );
}
